/**
 * Guarda Mundo - Serviços GM mobile carousel: indicador geral (bolinhas).
 */
import { scrollIntoView } from '@theme/scrolling';

if (!customElements.get('gm-servicos-carousel')) {
  class GmServicosCarousel extends HTMLElement {
    connectedCallback() {
      this._viewport = this.querySelector('.gm-mobile-peek__viewport');
      this._slides = [...this.querySelectorAll('.gm-mobile-peek__slide')];
      this._dotsNav = this.querySelector('.gm-mobile-servicos__dots');
      this._dots = this._dotsNav
        ? [...this._dotsNav.querySelectorAll('.gm-mobile-servicos__dot')]
        : [];

      if (!this._viewport || this._slides.length < 2 || !this._dots.length) return;

      this._activeIndex = 0;
      this._onScroll = () => {
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(() => this._syncFromScroll());
      };

      this._viewport.addEventListener('scroll', this._onScroll, { passive: true });

      this._dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.slideIndex, 10);
          if (!Number.isNaN(index)) this._goTo(index);
        });
      });

      this._observer = new IntersectionObserver(
        (entries) => this._syncFromIntersection(entries),
        {
          root: this._viewport,
          threshold: [0.45, 0.55, 0.65],
        }
      );

      this._slides.forEach((slide) => this._observer.observe(slide));

      this._resizeObserver = new ResizeObserver(() => this._syncFromScroll());
      this._resizeObserver.observe(this._viewport);

      this._syncFromScroll();
    }

    disconnectedCallback() {
      this._viewport?.removeEventListener('scroll', this._onScroll);
      this._observer?.disconnect();
      this._resizeObserver?.disconnect();
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    }

    _isCenteredPeek() {
      return this.classList.contains('gm-mobile-peek--centered');
    }

    _getLastIndex() {
      return this._slides.length - 1;
    }

    _getSnapScrollLeft(index) {
      const slide = this._slides[index];
      if (!slide || !this._viewport) return 0;

      if (this._isCenteredPeek()) {
        return Math.max(
          0,
          slide.offsetLeft - (this._viewport.clientWidth - slide.offsetWidth) / 2
        );
      }

      return slide.offsetLeft;
    }

    _getStride() {
      const { _slides: slides } = this;
      if (slides.length < 2) return slides[0]?.offsetWidth ?? 0;
      return slides[1].offsetLeft - slides[0].offsetLeft;
    }

    _syncFromIntersection(entries) {
      let best = null;

      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!best || entry.intersectionRatio > best.intersectionRatio) {
          best = entry;
        }
      }

      if (!best) return;

      const index = this._slides.indexOf(best.target);
      if (index >= 0) this._setActive(index);
    }

    _syncFromScroll() {
      if (this._isCenteredPeek()) {
        const scrollLeft = this._viewport.scrollLeft;
        let bestIndex = 0;
        let bestDistance = Infinity;

        for (let index = 0; index <= this._getLastIndex(); index += 1) {
          const distance = Math.abs(scrollLeft - this._getSnapScrollLeft(index));
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        }

        this._setActive(bestIndex);
        return;
      }

      const stride = this._getStride();
      if (stride <= 0) return;

      let index = Math.round(this._viewport.scrollLeft / stride);
      index = Math.max(0, Math.min(index, this._slides.length - 1));
      this._setActive(index);
    }

    _setActive(index) {
      if (index === this._activeIndex && this._dots[index]?.classList.contains('is-active')) return;

      this._activeIndex = index;

      this._dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    _goTo(index) {
      const slide = this._slides[index];
      if (!slide) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const inline = this._isCenteredPeek() ? 'center' : 'start';

      scrollIntoView(slide, {
        ancestor: this._viewport,
        inline,
        block: 'nearest',
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
      this._setActive(index);
    }
  }

  customElements.define('gm-servicos-carousel', GmServicosCarousel);
}
